import { useEffect, useState, type ReactNode } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

export const STYLE_RULES_MAX_LENGTH = 4000;

export type StyleRulesValues = {
  styleRules: string;
  styleRulesFont: string;
};

/**
 * Normalize only the edges of a rules field. Whitespace inside a sentence is
 * meaningful (and can be intentional in a prompt), so this must not collapse
 * or otherwise rewrite the user's text.
 */
export function normalizeStyleRulesText(value: unknown): string {
  if (typeof value !== "string") return "";
  return value.trim().slice(0, STYLE_RULES_MAX_LENGTH);
}

export function StyleRulesDialog({
  values,
  onSave,
  trigger,
  open,
  onOpenChange,
  isSaving = false,
  dialogTestId = "dialog-style-rules",
  bodyTestId,
  styleInputTestId = "input-style-rules",
  fontInputTestId = "input-style-rules-font",
  saveTestId = "button-save-style-rules",
  discardTestId = "button-discard-style-rules",
  saveLabel = "Save rules",
}: {
  values: StyleRulesValues;
  onSave: (values: StyleRulesValues) => void | boolean | Promise<void | boolean>;
  trigger?: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  isSaving?: boolean;
  dialogTestId?: string;
  bodyTestId?: string;
  styleInputTestId?: string;
  fontInputTestId?: string;
  saveTestId?: string;
  discardTestId?: string;
  saveLabel?: string;
}) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const [draft, setDraft] = useState<StyleRulesValues>(() => ({ ...values }));
  const [savingError, setSavingError] = useState(false);
  const isControlled = open !== undefined;
  const dialogOpen = isControlled ? open : uncontrolledOpen;

  // Rehydrate when a dialog is opened (or when the saved target changes while
  // it is open). Closing intentionally does not write anything back, so
  // unsaved edits are discarded on the next open.
  useEffect(() => {
    if (dialogOpen) {
      setDraft({ ...values });
      setSavingError(false);
    }
  }, [dialogOpen, values.styleRules, values.styleRulesFont]);

  const setDialogOpen = (nextOpen: boolean) => {
    if (!isControlled) setUncontrolledOpen(nextOpen);
    onOpenChange?.(nextOpen);
  };

  const save = async () => {
    setSavingError(false);
    try {
      const saved = await onSave({
        styleRules: normalizeStyleRulesText(draft.styleRules),
        styleRulesFont: normalizeStyleRulesText(draft.styleRulesFont),
      });
      // A target-aware owner can return false when a completion belongs to a
      // previous brand/epoch. That completion must not close the new target's
      // dialog or discard its draft.
      if (saved !== false) setDialogOpen(false);
    } catch {
      // The owner is responsible for its error toast. Keep the exact entered
      // draft in this component so a failed PATCH can be corrected/retried.
      setSavingError(true);
    }
  };

  return (
    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
      {trigger ? <DialogTrigger asChild>{trigger}</DialogTrigger> : null}
      <DialogContent
        className="sm:max-w-2xl max-h-[85vh] overflow-y-auto"
        data-testid={dialogTestId}
      >
        <DialogTitle>Style Rules</DialogTitle>
        <DialogDescription>
          Set the visual rules and typography preferences used by new Ads Express runs.
          Save applies both fields together.
        </DialogDescription>
        <div className="space-y-5" data-testid={bodyTestId}>
          <div className="space-y-2">
            <Label htmlFor="style-rules-dialog-style">Style Rules</Label>
            <p className="text-xs text-muted-foreground">
              Mandatory ad-generation rules for this brand — outfit requirements, no-go&apos;s,
              posing/background notes, and similar visual constraints. Treated as a hard
              constraint, not a suggestion; image models are instructed to follow it but
              cannot guarantee every detail in every render.
            </p>
            <Textarea
              id="style-rules-dialog-style"
              value={draft.styleRules}
              onChange={(event) =>
                setDraft((current) => ({
                  ...current,
                  styleRules: event.target.value.slice(0, STYLE_RULES_MAX_LENGTH),
                }))
              }
              maxLength={STYLE_RULES_MAX_LENGTH}
              rows={6}
              placeholder="Describe required styling, model direction, and absolute no-go&apos;s."
              disabled={isSaving}
              data-testid={styleInputTestId}
            />
            <div className="text-right text-xs tabular-nums text-muted-foreground">
              {draft.styleRules.length.toLocaleString()} / {STYLE_RULES_MAX_LENGTH.toLocaleString()}
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="style-rules-dialog-font">Font Rules</Label>
            <p className="text-xs text-muted-foreground">
              Headline/subhead/CTA typography preferences (font style, weight, size, case,
              or a named typeface). Best-effort guidance only — an image model cannot
              guarantee an exact typeface or point size.
            </p>
            <Textarea
              id="style-rules-dialog-font"
              value={draft.styleRulesFont}
              onChange={(event) =>
                setDraft((current) => ({
                  ...current,
                  styleRulesFont: event.target.value.slice(0, STYLE_RULES_MAX_LENGTH),
                }))
              }
              maxLength={STYLE_RULES_MAX_LENGTH}
              rows={4}
              placeholder="Describe headline, subhead, and CTA typography preferences."
              disabled={isSaving}
              data-testid={fontInputTestId}
            />
            <div className="text-right text-xs tabular-nums text-muted-foreground">
              {draft.styleRulesFont.length.toLocaleString()} / {STYLE_RULES_MAX_LENGTH.toLocaleString()}
            </div>
          </div>
          {savingError && (
            <p className="text-sm text-destructive" role="alert">
              Couldn&apos;t save these rules. Your edits are still here — try again.
            </p>
          )}
        </div>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            type="button"
            variant="ghost"
            onClick={() => setDialogOpen(false)}
            disabled={isSaving}
            data-testid={discardTestId}
          >
            Discard
          </Button>
          <Button
            type="button"
            onClick={save}
            disabled={isSaving}
            data-testid={saveTestId}
          >
            {isSaving && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
            {saveLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}